// ============================================================
// SideNav.js
// The portal's primary navigation. Renders as the persistent left
// rail on desktop, and as the contents of the off-canvas drawer on
// mobile — the same component either way, so the two surfaces can
// never offer different destinations.
//
// Active state derives from the current pathname via useLocation
// (no internal state). User widget at the bottom shows UPN and
// sign-out.
//
// Entry list and active-state rule live in navEntries.js, shared
// with the mobile top bar.
//
// Props (all optional — the component works standalone):
//   badges      Counts from useAdminBadgeCounts. When omitted the
//               component fetches its own, which is what keeps
//               <SideNav /> usable on its own in tests. AppShell
//               passes them down so the drawer opening does not fire
//               a duplicate pair of requests.
//   onNavigate  Called when a nav link is activated. The drawer uses
//               it to close itself; the desktop rail passes nothing.
// ============================================================

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LogOut } from 'lucide-react';
import useAdminBadgeCounts from '../hooks/useAdminBadgeCounts';
import {
  NAV_ENTRIES, PLATFORM_MGMT_ENTRIES, PLATFORM_ANALYTICS_ENTRIES, isEntryActive,
} from './navEntries';
import logo from '../assets/brand/lockup-horizontal-light.svg';
import './SideNav.css';

export default function SideNav({ badges, onNavigate }) {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const upn = account?.username || '';
  const displayName = account?.name || upn || 'Operator';

  // Only fetch when a parent has not already supplied the counts.
  const ownBadges = useAdminBadgeCounts({ enabled: badges === undefined });
  const badgeCounts = badges === undefined ? ownBadges : badges;

  function handleSignOut() {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  function renderEntry({ path, label, icon: Icon, badgeKey }) {
    const isActive = isEntryActive(path, location.pathname);
    const badgeCount = badgeKey ? (badgeCounts[badgeKey] || 0) : 0;
    return (
      <NavLink
        key={path}
        to={path}
        className={`side-nav__entry ${isActive ? 'side-nav__entry--active' : ''}`}
        aria-current={isActive ? 'page' : undefined}
        onClick={onNavigate}
      >
        <Icon size={18} strokeWidth={1.75} />
        <span>{label}</span>
        {badgeCount > 0 && (
          <span className="side-nav__badge" aria-label={`${badgeCount} new`}>{badgeCount}</span>
        )}
      </NavLink>
    );
  }

  return (
    <aside className="side-nav">
      <div className="side-nav__brand">
        <img src={logo} alt="A2Z CareHub" className="side-nav__logo" />
        <span className="side-nav__brand-admin">Admin</span>
      </div>

      <nav className="side-nav__entries" aria-label="Primary">
        {NAV_ENTRIES.map(renderEntry)}

        <div className="side-nav__section-header">Platform Management</div>
        {PLATFORM_MGMT_ENTRIES.map(renderEntry)}

        <div className="side-nav__section-header">Platform Analytics</div>
        {PLATFORM_ANALYTICS_ENTRIES.map(renderEntry)}
      </nav>

      <div className="side-nav__user">
        <div className="side-nav__user-info">
          <div className="side-nav__user-name" title={displayName}>{displayName}</div>
          <div className="side-nav__user-upn" title={upn}>{upn}</div>
        </div>
        <button
          type="button"
          className="side-nav__sign-out"
          onClick={handleSignOut}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
